import React from 'react'
import { Box, Chip, Divider, Link, Tooltip, Typography } from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import { handleLightspeedLinkClick } from '../../../utils/externalLinks'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'

export interface CountChipItem {
  id: string
  primary: string
  secondary?: string | null
  metrics?: Array<string | number | null | undefined>
  link?: string
}

interface CountChipProps {
  count: number
  items: CountChipItem[]
  label?: string
  metricHeaders?: string[]
  previewLimit?: number
  icon?: React.ReactElement
  emptyMuted?: boolean
  overrideLabel?: string
}

const TOOLTIP_COMPONENTS_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 3,
      maxWidth: 640,
      p: 1.5,
    },
  },
  arrow: {
    sx: {
      color: 'background.paper',
      '&::before': {
        border: '1px solid',
        borderColor: 'divider',
      },
    },
  },
}

const CountChip: React.FC<CountChipProps> = ({
  count,
  items,
  label,
  metricHeaders,
  previewLimit = 10,
  icon,
  emptyMuted = true,
  overrideLabel,
}) => {
  if (count === 0) {
    return (
      <Typography
        component="span"
        variant="body2"
        color={emptyMuted ? 'text.disabled' : 'text.primary'}
      >
        {overrideLabel ?? '0'}
      </Typography>
    )
  }

  const chipLabel = overrideLabel ?? count.toLocaleString()

  if (items.length === 0) {
    return (
      <Chip
        size="small"
        label={chipLabel}
        icon={icon}
        sx={{ fontWeight: 600, cursor: 'default' }}
      />
    )
  }

  const preview = items.slice(0, previewLimit)
  const remainder = items.length - preview.length
  const metricCount = metricHeaders?.length ?? 0
  const gridColumns = metricCount > 0
    ? `minmax(0, 1fr) ${'auto '.repeat(metricCount).trim()}`
    : 'minmax(0, 1fr)'

  const body = (
    <Box sx={{ minWidth: 240 }}>
      {label ? (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: 'text.secondary',
            display: 'block',
            mb: 0.75,
          }}
        >
          {label} · {count.toLocaleString()}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          columnGap: 2,
          rowGap: 0.75,
          alignItems: 'baseline',
        }}
      >
        {metricHeaders && metricHeaders.length > 0 ? (
          <>
            <Box />
            {metricHeaders.map((h, i) => (
              <Typography
                key={`h-${i}`}
                variant="caption"
                sx={{ textAlign: 'right', color: 'text.secondary', fontWeight: 600 }}
              >
                {h}
              </Typography>
            ))}
          </>
        ) : null}
        {preview.map((it) => (
          <React.Fragment key={it.id}>
            <Box sx={{ minWidth: 0 }}>
              {it.link ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <Link
                    href={it.link}
                    onClick={(e) =>
                      handleLightspeedLinkClick(e, it.link!, `Product ${it.secondary || it.primary}`)
                    }
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.25,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      direction: 'rtl',
                      textAlign: 'left',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <bdi>{it.primary}</bdi>
                  </Link>
                  <LaunchIcon sx={{ color: 'text.secondary', fontSize: 12, flexShrink: 0 }} />
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    direction: 'rtl',
                    textAlign: 'left',
                  }}
                >
                  <bdi>{it.primary}</bdi>
                </Typography>
              )}
              {it.secondary ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ...NUMERIC_FONT_SX, lineHeight: 1.25 }}
                >
                  {it.secondary}
                </Typography>
              ) : null}
            </Box>
            {(it.metrics ?? []).map((m, i) => (
              <Typography
                key={`m-${it.id}-${i}`}
                variant="body2"
                sx={{ textAlign: 'right', ...NUMERIC_FONT_SX, lineHeight: 1.25 }}
              >
                {m === null || m === undefined || m === '' ? '-' : m}
              </Typography>
            ))}
          </React.Fragment>
        ))}
      </Box>
      {remainder > 0 ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            + {remainder.toLocaleString()} more
          </Typography>
        </>
      ) : null}
    </Box>
  )

  return (
    <Tooltip title={body} arrow placement="top" componentsProps={TOOLTIP_COMPONENTS_PROPS}>
      <Chip
        size="small"
        label={chipLabel}
        icon={icon}
        sx={{
          fontWeight: 600,
          cursor: 'help',
          bgcolor: 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
        }}
      />
    </Tooltip>
  )
}

export default CountChip
